/* eslint-disable react/function-component-definition, react/destructuring-assignment */
import React, { useMemo } from "react";
import clsx from "clsx";
import { FILTER_TYPES } from "react-bootstrap-table2-filter";
import { Tooltip, OverlayTrigger } from "react-bootstrap";
import { Skeleton } from "../Skeleton";
import Menu from "./Menu";
import { createFilter, createFilterRenderer } from "./filters";
import styles from "./Table.scss";

const headerTooltip = ({ message, icon, className, placement }) => (
  <>
    &nbsp;
    <OverlayTrigger
      placement={placement || "top"}
      delay={{ show: 250, hide: 400 }}
      overlay={(tooltipProps) => (
        <Tooltip {...tooltipProps} className={className}>
          {message}
        </Tooltip>
      )}
    >
      <i
        className={clsx(
          icon || "fas fa-info-circle",
          styles.pointerEventsAuto,
          styles.cursorAuto
        )}
      />
    </OverlayTrigger>
  </>
);

const headerFormatter =
  (loading) =>
  (column, colIndex, { sortElement, filterElement }) =>
    (
      <div className="d-flex align-items-center">
        <div
          className={clsx(
            !filterElement && !column.tooltip && styles.ellipsis, // simple text title
            !filterElement && column.tooltip && styles.lineHeightNormal,
            column?.filter?.props?.type === FILTER_TYPES.MULTISELECT &&
              styles.multiselect, // multiselect
            !loading && filterElement && styles.pointerEventsAuto
          )}
        >
          {filterElement || column.text}
        </div>
        {column.tooltip ? headerTooltip(column.tooltip) : null}
        {sortElement && (
          <div
            className={clsx(
              "ml-1",
              styles.noWrap,
              !loading && styles.pointerEventsAuto
            )}
          >
            {sortElement}
          </div>
        )}
      </div>
    );

const loadingFormatter = () => <Skeleton />;

const createMenuColumn = (menu, loading, showMenu) => ({
  dataField: "",
  text: "",
  formatter: (cell, row, rowIndex, extraData) => (
    <Menu
      options={extraData.menu}
      row={row}
      loading={loading}
      showMenu={showMenu}
    />
  ),
  formatExtraData: { menu },
  headerClasses: styles.menuCell,
});

const useConfiguredColumns = ({
  columns,
  loading,
  readOnly,
  menu,
  showMenu,
  filtersOnChange,
}) => {
  const configuredColumns = useMemo(
    () =>
      columns
        .map(({ filter, filterRenderer, ...column }) => {
          if (filter) {
            // eslint-disable-next-line no-param-reassign
            filter.getFilter = (onChange) => {
              // eslint-disable-next-line no-param-reassign
              filtersOnChange[column.dataField] = onChange;
            };
          }
          return {
            headerFormatter: headerFormatter(loading),
            filter: createFilter(filter),
            filterRenderer: createFilterRenderer(filter, filterRenderer),
            ...column,
            formatter: loading ? loadingFormatter : column.formatter,
            formatExtraData: loading ? { loading } : column.formatExtraData,
          };
        })
        .concat(
          !readOnly && menu ? createMenuColumn(menu, loading, showMenu) : []
        ),
    [columns, loading, readOnly, menu, showMenu, filtersOnChange]
  );

  return configuredColumns;
};

export default useConfiguredColumns;
